import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Copy, Check, Pin, Archive as ArchiveIcon, ChevronDown, Gift, Download, CheckCircle2, Flame, Minus, ThumbsDown, Send, Plus, X, Loader2, FileText, RefreshCw, Pencil, Save } from "lucide-react";
import { listScripts, deleteScript, updateScript, saveScript, generateLeadMagnet, CATEGORIES, TIPI } from "@/lib/api";
import { exportMagnetPdf } from "@/lib/pdf";
import { TipoKpi } from "@/components/Ideas";
import { StorieBox } from "@/components/StorieBox";
import { toast } from "sonner";

const FILTERS = ["Tutte", ...CATEGORIES];
const ESITO_META = {
  bene: { label: "Andato bene", color: "#16A34A", Icon: Flame },
  media: { label: "Nella media", color: "#D97706", Icon: Minus },
  flop: { label: "Flop", color: "#DC2626", Icon: ThumbsDown },
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Archive() {
  const [filter, setFilter] = useState("Tutte");
  const [statusFilter, setStatusFilter] = useState("da_pubblicare");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [magnetLoadingId, setMagnetLoadingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const emptyAdd = { categoria: "Tabù di coppia", tipo: "Attenzione", tema: "", hook_fissato: "", copione_completo: "", pubblicato: true, esito: "bene" };
  const [addForm, setAddForm] = useState(emptyAdd);

  const load = async (f) => {
    setLoading(true);
    try {
      const data = await listScripts(f);
      setItems(data);
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter);
  }, [filter]);

  const onDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteScript(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Eliminato");
    } catch {
      toast.error("Errore");
    }
  };

  const onMarkPublished = async (id, e) => {
    e.stopPropagation();
    try {
      const updated = await updateScript(id, { pubblicato: true });
      setItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
      toast.success("Segnato come pubblicato");
    } catch {
      toast.error("Errore");
    }
  };

  const onSetEsito = async (id, esito) => {
    try {
      const updated = await updateScript(id, { esito });
      setItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
      toast.success("Esito salvato — le prossime idee ne terranno conto");
    } catch {
      toast.error("Errore");
    }
  };

  const startEdit = (item, e) => {
    e.stopPropagation();
    setExpanded(item.id);
    setEditingId(item.id);
    setEditForm({
      categoria: item.categoria || "Tabù di coppia",
      tipo: item.tipo || "Attenzione",
      tema: item.tema || "",
      hook_fissato: item.hook_fissato || "",
      copione_completo: item.copione_completo || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async (id) => {
    setSavingEdit(true);
    try {
      const updated = await updateScript(id, editForm);
      setItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
      setEditingId(null);
      setEditForm(null);
      toast.success("Modifiche salvate");
    } catch {
      toast.error("Errore nel salvataggio delle modifiche");
    } finally {
      setSavingEdit(false);
    }
  };

  const onCreateMagnet = async (item) => {
    setMagnetLoadingId(item.id);
    try {
      const magnet = await generateLeadMagnet({ tema: item.tema, categoria: item.categoria, risorsa: item.risorsa || "" });
      const updated = await updateScript(item.id, { lead_magnet: magnet });
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
      toast.success("Risorsa creata — ora puoi esportare il PDF");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 502 || status === 504 || !e?.response) {
        toast.error("La risorsa ci sta mettendo troppo. Riprova tra un attimo: il pulsante è di nuovo attivo.");
      } else {
        toast.error(e?.response?.data?.detail || "Errore nella creazione della risorsa");
      }
    } finally {
      setMagnetLoadingId(null);
    }
  };

  const pubDate = (i) => i.published_at || i.created_at;
  const visible = items
    .filter((i) => (statusFilter === "pubblicati" ? i.pubblicato : !i.pubblicato))
    .sort((a, b) => (statusFilter === "pubblicati" ? new Date(pubDate(b)) - new Date(pubDate(a)) : 0));

  const pubblicati = items.filter((i) => i.pubblicato);
  const nBene = pubblicati.filter((i) => i.esito === "bene").length;
  const nFlop = pubblicati.filter((i) => i.esito === "flop").length;

  const now = new Date();
  const pubblicatiMese = pubblicati.filter((i) => {
    const d = new Date(pubDate(i));
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  });
  const funnelMese = TIPI.map((t) => ({ tipo: t, count: pubblicatiMese.filter((i) => i.tipo === t).length }));
  const meseLabel = now.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const TIPO_COLOR = { Attenzione: "#D92D20", Educazione: "#2563EB", Autorevolezza: "#D97706", Conversione: "#16A34A" };

  const onAddSubmit = async () => {
    if (!addForm.tema.trim() && !addForm.hook_fissato.trim()) {
      toast.error("Aggiungi almeno il tema o l'hook");
      return;
    }
    try {
      const saved = await saveScript(addForm);
      setItems((prev) => [saved, ...prev]);
      setStatusFilter(addForm.pubblicato ? "pubblicati" : "da_pubblicare");
      setShowAdd(false);
      setAddForm(emptyAdd);
      toast.success("Reel aggiunto all'archivio");
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const onCopyAll = (item, e) => {
    e.stopPropagation();
    const text = `${item.hook_fissato}\n\n${item.copione_completo || ""}\n\n${item.caption || ""}\n\n${(item.hashtags || []).join(" ")}`;
    navigator.clipboard.writeText(text);
    setCopiedId(item.id);
    toast.success("Copiato tutto");
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex gap-1 bg-[#121212] border border-[#262626] rounded-md p-1 w-fit">
          {[
            { id: "da_pubblicare", label: "Da pubblicare" },
            { id: "pubblicati", label: "Pubblicati" },
          ].map((s) => (
            <button
              key={s.id}
              data-testid={`status-${s.id}`}
              onClick={() => setStatusFilter(s.id)}
              className={`text-sm font-semibold py-2 px-4 rounded-sm transition-colors ${
                statusFilter === s.id ? "bg-[#D92D20] text-white" : "text-neutral-400 hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button
          data-testid="add-reel-btn"
          onClick={() => { setShowAdd((v) => !v); setAddForm(emptyAdd); }}
          className="border border-[#333] hover:border-[#D92D20] text-neutral-300 hover:text-white font-semibold py-2 px-4 rounded-sm flex items-center gap-2 transition-colors text-sm"
        >
          <Plus size={16} /> Aggiungi reel esistente
        </button>
      </div>

      {showAdd && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="bg-[#121212] border border-[#262626] rounded-md p-6 mb-6" data-testid="add-reel-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg">Aggiungi un reel già pubblicato</h3>
            <button onClick={() => setShowAdd(false)} className="text-neutral-500 hover:text-white"><X size={18} /></button>
          </div>

          <label className="label-caps block mb-2">Categoria</label>
          <div className="flex flex-wrap gap-2 mb-4">
            {CATEGORIES.map((c) => (
              <button key={c} data-testid={`add-cat-${c}`} onClick={() => setAddForm({ ...addForm, categoria: c })}
                className={`text-xs font-semibold py-2 px-3 rounded-sm border ${addForm.categoria === c ? "bg-[#D92D20] border-[#D92D20] text-white" : "border-[#333] text-neutral-400"}`}>{c}</button>
            ))}
          </div>

          <label className="label-caps block mb-2">Tipo di contenuto</label>
          <div className="flex flex-wrap gap-2 mb-4">
            {TIPI.map((t) => (
              <button key={t} data-testid={`add-tipo-${t}`} onClick={() => setAddForm({ ...addForm, tipo: t })}
                className={`text-xs font-semibold py-2 px-3 rounded-sm border ${addForm.tipo === t ? "bg-white border-white text-black" : "border-[#333] text-neutral-400"}`}>{t}</button>
            ))}
          </div>

          <label className="label-caps block mb-2">Tema / di cosa parla</label>
          <input data-testid="add-tema" value={addForm.tema} onChange={(e) => setAddForm({ ...addForm, tema: e.target.value })}
            placeholder="Es. l'amante in ufficio come unica adrenalina rimasta"
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] mb-4" />

          <label className="label-caps block mb-2">Hook fissato (opzionale)</label>
          <input data-testid="add-hook" value={addForm.hook_fissato} onChange={(e) => setAddForm({ ...addForm, hook_fissato: e.target.value })}
            placeholder="LA FRASE FISSATA IN ALTO NEL REEL"
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] mb-4" />

          <label className="label-caps block mb-2">Script / copione (opzionale — incollalo qui)</label>
          <textarea data-testid="add-copione" value={addForm.copione_completo} onChange={(e) => setAddForm({ ...addForm, copione_completo: e.target.value })} rows={4}
            placeholder="Incolla qui il testo del reel..."
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] mb-4 resize-none" />

          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input type="checkbox" data-testid="add-pubblicato" checked={addForm.pubblicato} onChange={(e) => setAddForm({ ...addForm, pubblicato: e.target.checked })} className="accent-[#D92D20]" />
            <span className="text-sm text-neutral-300">Già pubblicato</span>
          </label>

          {addForm.pubblicato && (
            <>
              <label className="label-caps block mb-2">Com'è andato?</label>
              <div className="flex flex-wrap gap-2 mb-5">
                {Object.entries(ESITO_META).map(([key, m]) => {
                  const active = addForm.esito === key;
                  return (
                    <button key={key} data-testid={`add-esito-${key}`} onClick={() => setAddForm({ ...addForm, esito: key })}
                      className="text-sm font-semibold py-2 px-3 rounded-sm border flex items-center gap-1.5"
                      style={active ? { background: `${m.color}22`, borderColor: m.color, color: m.color } : { borderColor: "#333", color: "#9CA3AF" }}>
                      <m.Icon size={14} /> {m.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <button data-testid="add-submit" onClick={onAddSubmit}
            className="bg-[#D92D20] hover:bg-[#B42318] text-white font-bold py-2.5 px-6 rounded-sm text-sm uppercase tracking-wider">
            Aggiungi all'archivio
          </button>
        </motion.div>
      )}

      {statusFilter === "pubblicati" && pubblicati.length > 0 && (
        <div className="flex flex-wrap gap-4 mb-5 text-sm">
          <span className="text-neutral-400">Pubblicati: <b className="text-white">{pubblicati.length}</b></span>
          <span style={{ color: "#16A34A" }}>🔥 Andati bene: <b>{nBene}</b></span>
          <span style={{ color: "#DC2626" }}>👎 Flop: <b>{nFlop}</b></span>
        </div>
      )}

      {statusFilter === "pubblicati" && filter === "Tutte" && (
        <div className="bg-[#121212] border border-[#262626] rounded-md p-4 mb-6" data-testid="funnel-counter">
          <div className="flex items-center justify-between mb-3">
            <span className="label-caps">Copertura funnel · <span className="capitalize text-neutral-300">{meseLabel}</span></span>
            <span className="text-xs text-neutral-600">{pubblicatiMese.length} pubblicati questo mese</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {funnelMese.map(({ tipo, count }) => (
              <div
                key={tipo}
                data-testid={`funnel-${tipo}`}
                className="rounded-sm border p-3 flex flex-col gap-1"
                style={{ borderColor: count > 0 ? TIPO_COLOR[tipo] : "#262626", background: count > 0 ? `${TIPO_COLOR[tipo]}12` : "transparent" }}
              >
                <span className="font-display font-extrabold text-2xl leading-none" style={{ color: count > 0 ? TIPO_COLOR[tipo] : "#525252" }}>{count}</span>
                <span className="text-[0.7rem] uppercase tracking-wider font-semibold text-neutral-400">{tipo}</span>
              </div>
            ))}
          </div>
          {funnelMese.some((f) => f.count === 0) && (
            <p className="text-xs text-neutral-600 mt-3">
              Ti manca ancora: {funnelMese.filter((f) => f.count === 0).map((f) => f.tipo).join(", ")}. Copri tutto il funnel per una strategia a 360°.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-sm font-semibold py-2 px-4 rounded-sm border transition-colors ${
              filter === f
                ? "bg-white text-black border-white"
                : "bg-transparent border-[#333] text-neutral-400 hover:border-neutral-500"
            }`}
          >
            {f}
          </button>
        ))}
        <span className="text-xs text-neutral-600 ml-auto">{visible.length} script</span>
      </div>

      {loading ? (
        <p className="text-neutral-500">Carico...</p>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-[#262626] rounded-md py-20 flex flex-col items-center text-center">
          <ArchiveIcon size={36} className="text-neutral-700 mb-3" />
          <p className="text-neutral-500">
            {statusFilter === "pubblicati" ? "Nessuno script pubblicato ancora." : "Nessuno script da pubblicare in questa categoria."}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="archive-list">
          {visible.map((item) => {
            const open = expanded === item.id;
            const esitoMeta = item.esito ? ESITO_META[item.esito] : null;
            return (
              <div key={item.id} className="bg-[#121212] border border-[#262626] rounded-md overflow-hidden">
                <button
                  data-testid={`archive-item-${item.id}`}
                  onClick={() => setExpanded(open ? null : item.id)}
                  className="w-full text-left p-5 flex items-start gap-4 hover:bg-[#161616] transition-colors"
                >
                  <Pin size={16} className="text-[#D92D20] mt-1 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-lg leading-tight text-white truncate">
                      {item.hook_fissato || item.tema}
                    </p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="text-[0.65rem] uppercase tracking-wider font-semibold text-[#D92D20] bg-[#D92D20]/10 px-2 py-0.5 rounded-sm">
                        {item.categoria}
                      </span>
                      {item.tipo && (
                        <span className="text-[0.65rem] uppercase tracking-wider font-semibold text-neutral-400 border border-[#333] px-2 py-0.5 rounded-sm">
                          {item.tipo}
                        </span>
                      )}
                      {esitoMeta && (
                        <span className="text-[0.65rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm flex items-center gap-1" style={{ color: esitoMeta.color, background: `${esitoMeta.color}1f` }}>
                          <esitoMeta.Icon size={11} /> {esitoMeta.label}
                        </span>
                      )}
                      {item.lead_magnet && (
                        <span
                          data-testid={`magnet-badge-${item.id}`}
                          className="text-[0.65rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm flex items-center gap-1 text-[#22C55E] bg-[#16A34A]/15 border border-[#16A34A]/40"
                          title="Risorsa gratuita pronta"
                        >
                          <Gift size={11} /> Risorsa pronta
                        </span>
                      )}
                      <span className="text-xs text-neutral-600">{fmtDate(item.published_at || item.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {!item.pubblicato && (
                      <span
                        data-testid={`publish-${item.id}`}
                        onClick={(e) => onMarkPublished(item.id, e)}
                        className="text-neutral-400 hover:text-[#16A34A] transition-colors flex items-center gap-1 text-xs font-semibold"
                        title="Segna come pubblicato"
                      >
                        <Send size={15} /> <span className="hidden sm:inline">Pubblicato</span>
                      </span>
                    )}
                    <span onClick={(e) => onCopyAll(item, e)} className="text-neutral-500 hover:text-white transition-colors" title="Copia tutto">
                      {copiedId === item.id ? <Check size={16} /> : <Copy size={16} />}
                    </span>
                    <span
                      data-testid={`edit-${item.id}`}
                      onClick={(e) => startEdit(item, e)}
                      className="text-neutral-500 hover:text-[#D92D20] transition-colors"
                      title="Modifica hook e script"
                    >
                      <Pencil size={16} />
                    </span>
                    <span
                      data-testid={`delete-${item.id}`}
                      onClick={(e) => onDelete(item.id, e)}
                      className="text-neutral-500 hover:text-[#D92D20] transition-colors"
                      title="Elimina"
                    >
                      <Trash2 size={16} />
                    </span>
                    <ChevronDown size={18} className={`text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`} />
                  </div>
                </button>
                <AnimatePresence>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 pt-1 border-t border-[#262626] space-y-4">
                        {editingId === item.id && editForm && (
                          <div className="bg-[#0A0A0A] border border-[#D92D20]/40 rounded-sm p-4 space-y-3" data-testid={`edit-form-${item.id}`}>
                            <div className="flex items-center justify-between">
                              <span className="label-caps text-[#D92D20] flex items-center gap-2">
                                <Pencil size={13} /> Modifica reel
                              </span>
                              <span className="text-xs text-neutral-600">Allinea l'archivio a ciò che hai pubblicato davvero</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="label-caps block mb-1">Categoria</label>
                                <select
                                  data-testid={`edit-categoria-${item.id}`}
                                  value={editForm.categoria}
                                  onChange={(e) => setEditForm({ ...editForm, categoria: e.target.value })}
                                  className="w-full bg-[#121212] border border-[#333] rounded-sm p-2 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20]"
                                >
                                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="label-caps block mb-1">Tipo</label>
                                <select
                                  data-testid={`edit-tipo-${item.id}`}
                                  value={editForm.tipo}
                                  onChange={(e) => setEditForm({ ...editForm, tipo: e.target.value })}
                                  className="w-full bg-[#121212] border border-[#333] rounded-sm p-2 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20]"
                                >
                                  {TIPI.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                            </div>
                            <div>
                              <label className="label-caps block mb-1">Hook fissato</label>
                              <textarea
                                data-testid={`edit-hook-${item.id}`}
                                value={editForm.hook_fissato}
                                onChange={(e) => setEditForm({ ...editForm, hook_fissato: e.target.value })}
                                rows={2}
                                className="w-full bg-[#121212] border border-[#333] rounded-sm p-2 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] resize-none"
                              />
                            </div>
                            <div>
                              <label className="label-caps block mb-1">Tema / Angolo</label>
                              <textarea
                                data-testid={`edit-tema-${item.id}`}
                                value={editForm.tema}
                                onChange={(e) => setEditForm({ ...editForm, tema: e.target.value })}
                                rows={2}
                                className="w-full bg-[#121212] border border-[#333] rounded-sm p-2 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] resize-none"
                              />
                            </div>
                            <div>
                              <label className="label-caps block mb-1">Script completo</label>
                              <textarea
                                data-testid={`edit-copione-${item.id}`}
                                value={editForm.copione_completo}
                                onChange={(e) => setEditForm({ ...editForm, copione_completo: e.target.value })}
                                rows={10}
                                className="w-full bg-[#121212] border border-[#333] rounded-sm p-3 text-sm text-neutral-100 leading-relaxed focus:outline-none focus:border-[#D92D20] resize-y"
                              />
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                data-testid={`edit-save-${item.id}`}
                                onClick={() => saveEdit(item.id)}
                                disabled={savingEdit}
                                className="bg-[#D92D20] hover:bg-[#b91c13] disabled:opacity-60 text-white font-bold py-2 px-5 rounded-sm flex items-center gap-2 transition-colors text-sm uppercase tracking-wider"
                              >
                                {savingEdit ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                                {savingEdit ? "Salvo..." : "Salva modifiche"}
                              </button>
                              <button
                                data-testid={`edit-cancel-${item.id}`}
                                onClick={cancelEdit}
                                className="text-neutral-400 hover:text-white transition-colors text-sm font-semibold flex items-center gap-1.5"
                              >
                                <X size={15} /> Annulla
                              </button>
                            </div>
                          </div>
                        )}
                        {editingId !== item.id && item.pubblicato && (
                          <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
                            <span className="label-caps block mb-2">Com'è andato questo reel?</span>
                            <TipoKpi tipo={item.tipo} className="mb-3" />
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(ESITO_META).map(([key, m]) => {
                                const active = item.esito === key;
                                return (
                                  <button
                                    key={key}
                                    data-testid={`esito-${key}-${item.id}`}
                                    onClick={() => onSetEsito(item.id, key)}
                                    className="text-sm font-semibold py-2 px-3 rounded-sm border flex items-center gap-1.5 transition-colors"
                                    style={active
                                      ? { background: `${m.color}22`, borderColor: m.color, color: m.color }
                                      : { borderColor: "#333", color: "#9CA3AF" }}
                                  >
                                    <m.Icon size={14} /> {m.label}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="text-xs text-neutral-600 mt-2">Le idee del giorno impareranno da questo: più temi come quelli "andati bene", meno come i "flop".</p>
                          </div>
                        )}
                        {editingId !== item.id && item.copione_completo && (
                          <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
                            <div className="flex items-center justify-between mb-1">
                              <span className="label-caps flex items-center gap-2">
                                <Pin size={12} className="text-[#D92D20]" /> Script completo
                              </span>
                              <button
                                data-testid={`copy-copione-${item.id}`}
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(item.copione_completo); toast.success("Script copiato"); }}
                                className="text-neutral-500 hover:text-white transition-colors"
                                title="Copia script"
                              >
                                <Copy size={15} />
                              </button>
                            </div>
                            <p className="text-neutral-200 leading-relaxed whitespace-pre-wrap" data-testid={`copione-${item.id}`}>{item.copione_completo}</p>
                          </div>
                        )}
                        {[
                          ["Notizia nuda", item.hook],
                          ["Fatto + scena di casa", item.story],
                          ["Paradosso + tesi", item.svolta],
                          ["Concessione", item.obiezione],
                          ["Punchline", item.punchline],
                          ["Frase inoltrabile", item.frase_inoltrabile],
                          ["CTA", item.cta],
                        ].map(([label, val]) =>
                          val ? (
                            <div key={label} className="border-l-2 border-[#333] pl-3">
                              <span className="label-caps">{label}</span>
                              <p className="text-neutral-300 leading-relaxed whitespace-pre-wrap mt-1">{val}</p>
                            </div>
                          ) : null
                        )}
                        {item.caption && (
                          <div className="pt-2">
                            <span className="label-caps">Caption</span>
                            <p className="text-neutral-400 leading-relaxed whitespace-pre-wrap mt-1">{item.caption}</p>
                          </div>
                        )}
                        {item.hashtags?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {item.hashtags.map((h, i) => (
                              <span key={i} className="text-xs text-[#D92D20]">{h}</span>
                            ))}
                          </div>
                        )}
                        {editingId !== item.id && (
                          <StorieBox item={item} onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))} />
                        )}
                        {!item.lead_magnet && (item.tipo === "Conversione" || item.risorsa || item.parola_chiave) && (
                          <div className="bg-[#0e1a12] border border-[#16A34A]/40 rounded-sm p-4 mt-2" data-testid={`create-magnet-box-${item.id}`}>
                            <span className="label-caps text-[#22C55E] flex items-center gap-2 mb-2">
                              <Gift size={13} /> Risorsa gratuita di conversione
                            </span>
                            {item.risorsa && (
                              <p className="text-neutral-300 text-sm mb-1"><span className="text-neutral-500">Risorsa prevista:</span> {item.risorsa}</p>
                            )}
                            {item.parola_chiave && (
                              <p className="text-neutral-300 text-sm mb-3"><span className="text-neutral-500">Parola chiave DM:</span> <span className="font-bold text-white">{item.parola_chiave}</span></p>
                            )}
                            <p className="text-xs text-neutral-500 mb-3">Questo reel non ha ancora la risorsa da regalare in DM. Creala per poterla esportare in PDF.</p>
                            <button
                              data-testid={`create-magnet-${item.id}`}
                              onClick={() => onCreateMagnet(item)}
                              disabled={magnetLoadingId === item.id}
                              className="bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-60 text-white font-bold py-2.5 px-5 rounded-sm flex items-center gap-2 transition-colors text-sm uppercase tracking-wider"
                            >
                              {magnetLoadingId === item.id ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />}
                              {magnetLoadingId === item.id ? "Creo la risorsa..." : "Crea la risorsa gratuita"}
                            </button>
                          </div>
                        )}
                        {item.lead_magnet && (
                          <div className="bg-[#0e1a12] border border-[#16A34A]/40 rounded-sm p-4 mt-2">
                            <div className="flex items-center justify-between mb-2">
                              <span className="label-caps text-[#22C55E] flex items-center gap-2">
                                <Gift size={13} /> Risorsa gratuita
                              </span>
                              <div className="flex items-center gap-4">
                                <button
                                  data-testid={`regenerate-magnet-${item.id}`}
                                  onClick={() => onCreateMagnet(item)}
                                  disabled={magnetLoadingId === item.id}
                                  className="text-sm font-semibold text-[#22C55E] hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-60"
                                  title="Rigenera la risorsa nel nuovo formato (con sezione soluzione)"
                                >
                                  {magnetLoadingId === item.id ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                                  {magnetLoadingId === item.id ? "Rigenero..." : "Rigenera (nuovo formato)"}
                                </button>
                                <button
                                  data-testid={`export-pdf-${item.id}`}
                                  onClick={() => exportMagnetPdf(item.lead_magnet)}
                                  className="text-sm font-semibold text-neutral-300 hover:text-white transition-colors flex items-center gap-1.5"
                                >
                                  <Download size={14} /> Esporta PDF
                                </button>
                              </div>
                            </div>
                            <p className="font-display font-bold text-white leading-tight">{item.lead_magnet.titolo}</p>
                            {item.lead_magnet.formato && (
                              <p className="text-xs text-neutral-500 mt-0.5">{item.lead_magnet.formato}</p>
                            )}
                            {item.lead_magnet.contenuto && (
                              <p className="text-neutral-400 leading-relaxed whitespace-pre-wrap mt-2 text-sm">{item.lead_magnet.contenuto}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
